import { Reducer } from 'redux'
import { createSelector } from 'reselect'
import { ICostControlConfig, ICostControlConfigState } from '../../types/costControlConfig'
import {
  GET_COST_CONTROL_CONFIGS_ACTIONS as g,
  ADD_COST_CONTROL_CONFIG_MODAL_ACTIONS as m,
  DELETE_COST_CONTROL_CONFIG_MODAL_ACTIONS as dm,
  CREATE_COST_CONTROL_CONFIG_ACTIONS as c,
  UPDATE_COST_CONTROL_CONFIG_ACTIONS as u,
  DELETE_COST_CONTROL_CONFIG_ACTIONS as d,
} from '../actions/costControlConfig'
import { IStore } from './types'

const defaultState: ICostControlConfigState = {
  error: undefined,
  fetching: false,
  costControlConfigs: [],
  costControlConfig: undefined,
  modalOpen: false,
  modalError: undefined,
  deleteModalOpen: false,
  configToDelete: undefined,
  saving: false,
}

type CostControlConfigAction =
  | { type: g.GET_COST_CONTROL_CONFIGS_REQUEST }
  | {
      type: g.GET_COST_CONTROL_CONFIGS_SUCCESS
      payload: { costControlConfigs: ICostControlConfig[] }
    }
  | { type: g.GET_COST_CONTROL_CONFIGS_FAILURE; error: string }
  | {
      type: m.ADD_COST_CONTROL_CONFIG_MODAL_OPEN
      payload: { costControlConfig?: ICostControlConfig }
    }
  | { type: m.ADD_COST_CONTROL_CONFIG_MODAL_CLOSE }
  | {
      type: dm.DELETE_COST_CONTROL_CONFIG_MODAL_OPEN
      payload: { costControlConfig: ICostControlConfig }
    }
  | { type: dm.DELETE_COST_CONTROL_CONFIG_MODAL_CLOSE }
  | { type: c.CREATE_COST_CONTROL_CONFIG_REQUEST }
  | {
      type: c.CREATE_COST_CONTROL_CONFIG_SUCCESS
      payload: { costControlConfig: ICostControlConfig }
    }
  | { type: c.CREATE_COST_CONTROL_CONFIG_FAILURE; error: string }
  | { type: u.UPDATE_COST_CONTROL_CONFIG_REQUEST }
  | {
      type: u.UPDATE_COST_CONTROL_CONFIG_SUCCESS
      payload: { costControlConfig: ICostControlConfig }
    }
  | { type: u.UPDATE_COST_CONTROL_CONFIG_FAILURE; error: string }
  | { type: d.DELETE_COST_CONTROL_CONFIG_REQUEST }
  | { type: d.DELETE_COST_CONTROL_CONFIG_SUCCESS }
  | { type: d.DELETE_COST_CONTROL_CONFIG_FAILURE; error: string }

const costControlConfigReducer: Reducer<ICostControlConfigState, CostControlConfigAction> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case g.GET_COST_CONTROL_CONFIGS_REQUEST: {
      return {
        ...state,
        error: '',
        fetching: true,
      }
    }
    case g.GET_COST_CONTROL_CONFIGS_FAILURE: {
      const { error } = action
      return {
        ...state,
        error,
        fetching: false,
      }
    }
    case g.GET_COST_CONTROL_CONFIGS_SUCCESS: {
      const { costControlConfigs } = action.payload
      return {
        ...state,
        fetching: false,
        error: '',
        costControlConfigs,
      }
    }
    case m.ADD_COST_CONTROL_CONFIG_MODAL_OPEN: {
      const { costControlConfig } = action.payload
      return {
        ...state,
        modalOpen: true,
        costControlConfig,
      }
    }
    case m.ADD_COST_CONTROL_CONFIG_MODAL_CLOSE: {
      return {
        ...state,
        modalOpen: false,
        modalError: undefined,
        costControlConfig: undefined,
      }
    }
    case dm.DELETE_COST_CONTROL_CONFIG_MODAL_OPEN: {
      const { costControlConfig } = action.payload
      return {
        ...state,
        deleteModalOpen: true,
        configToDelete: costControlConfig,
      }
    }
    case dm.DELETE_COST_CONTROL_CONFIG_MODAL_CLOSE: {
      return {
        ...state,
        deleteModalOpen: false,
        modalError: undefined,
        configToDelete: undefined,
      }
    }
    case c.CREATE_COST_CONTROL_CONFIG_REQUEST:
    case u.UPDATE_COST_CONTROL_CONFIG_REQUEST:
    case d.DELETE_COST_CONTROL_CONFIG_REQUEST: {
      return {
        ...state,
        modalError: undefined,
        saving: true,
      }
    }
    case c.CREATE_COST_CONTROL_CONFIG_FAILURE:
    case u.UPDATE_COST_CONTROL_CONFIG_FAILURE:
    case d.DELETE_COST_CONTROL_CONFIG_FAILURE: {
      const { error } = action
      return {
        ...state,
        saving: false,
        modalError: error,
      }
    }
    case c.CREATE_COST_CONTROL_CONFIG_SUCCESS: {
      const { costControlConfig } = action.payload
      return {
        ...state,
        saving: false,
        modalOpen: false,
        costControlConfigs: [...state.costControlConfigs, costControlConfig],
      }
    }
    case u.UPDATE_COST_CONTROL_CONFIG_SUCCESS: {
      const { costControlConfig } = action.payload
      const costControlConfigs = state.costControlConfigs.map(config =>
        config.id === costControlConfig.id ? costControlConfig : config
      )
      return {
        ...state,
        saving: false,
        modalOpen: false,
        costControlConfigs,
      }
    }
    case d.DELETE_COST_CONTROL_CONFIG_SUCCESS: {
      return {
        ...state,
        saving: false,
        deleteModalOpen: false,
        costControlConfigs: state.costControlConfigs.filter(
          config => config.id !== state.configToDelete?.id
        ),
        configToDelete: undefined,
      }
    }
    default:
      return state
  }
}

export const costControlConfigSelector = (state: IStore): ICostControlConfigState =>
  state.costControlConfig

export const selectCostControlConfigError = createSelector(
  costControlConfigSelector,
  ({ error }) => error
)

export const selectCostControlConfigFetching = createSelector(
  costControlConfigSelector,
  ({ fetching }) => fetching
)

export const selectCostControlConfigs = createSelector(
  costControlConfigSelector,
  ({ costControlConfigs }) => costControlConfigs
)

export const selectCostControlConfig = createSelector(
  costControlConfigSelector,
  ({ costControlConfig }) => costControlConfig
)

export const selectCostControlConfigModalError = createSelector(
  costControlConfigSelector,
  ({ modalError }) => modalError
)

export const selectDeleteModalOpen = createSelector(
  costControlConfigSelector,
  ({ deleteModalOpen }) => deleteModalOpen
)

export const selectConfigToDelete = createSelector(
  costControlConfigSelector,
  ({ configToDelete }) => configToDelete
)

export const selectCostControlConfigSaving = createSelector(
  costControlConfigSelector,
  ({ saving }) => saving
)

export const selectCostControlConfigModalOpen = createSelector(
  costControlConfigSelector,
  ({ modalOpen }) => modalOpen
)

export default costControlConfigReducer
